import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarPlus, Clock, MapPin, Settings2 } from "lucide-react";
import { api, fmtDate, todayISO } from "@/lib/api";
import { MonthCalendar, monthRange } from "@/components/MonthCalendar";
import { AppointmentDialog } from "@/components/AppointmentDialog";
import { AppointmentStatus, PaymentBadge, statusTone } from "@/components/AppointmentStatus";
import { AvailabilityDialog } from "@/components/AvailabilityDialog";

export default function Appointments() {
  const now = new Date();
  const [month, setMonth] = useState(new Date(now.getFullYear(), now.getMonth(), 1));
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(todayISO());
  const [open, setOpen] = useState(false);
  const [edit, setEdit] = useState(null);
  const [avOpen, setAvOpen] = useState(false);

  const load = useCallback(() => api.get("/appointments", { params: monthRange(month) }).then((r) => setItems(r.data)), [month]);
  useEffect(() => { load(); }, [load]);

  const dayItems = items.filter((a) => a.date === selected);
  const events = items.map((a) => ({ id: a.id, date: a.date, time: a.time, label: a.patient_name, tone: statusTone(a.status), raw: a }));

  return (
    <div className="fade-up space-y-8" data-testid="appointments-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="label-xs text-[#3d655b]">Calendario pazienti</div>
          <h1 className="page-title mt-1">Appuntamenti</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <button className="btn-outline" onClick={() => setAvOpen(true)} data-testid="availability-open-button"><Settings2 className="h-4 w-4" /> Orari disponibili</button>
          <button className="btn-primary" onClick={() => { setEdit(null); setOpen(true); }} data-testid="fissa-appuntamento-btn"><CalendarPlus className="h-4 w-4" /> Fissa appuntamento</button>
        </div>
      </div>
      <div className="grid gap-6 xl:grid-cols-[1fr_340px]">
        <MonthCalendar month={month} onMonthChange={setMonth} events={events} selected={selected} onSelectDay={setSelected}
          onEventClick={(e) => { setEdit(e.raw); setOpen(true); }} prefix="appointments" />
        <div className="card h-fit" data-testid="appointments-day-panel">
          <div className="label-xs">Giorno selezionato</div>
          <h3 className="mt-1 text-lg font-bold capitalize">{fmtDate(selected, { weekday: "long", day: "numeric", month: "long" })}</h3>
          <div className="mt-4 space-y-3">
            {dayItems.length ? dayItems.map((a) => (
              <button key={a.id} onClick={() => { setEdit(a); setOpen(true); }} className="w-full rounded-xl border border-[#e2e8f0] p-3 text-left hover:border-[#b8956f]" data-testid={`day-appt-${a.id}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{a.patient_name}</span>
                  <span className="flex items-center gap-1 text-sm font-semibold text-[#3d655b]"><Clock className="h-3.5 w-3.5" />{a.time}</span>
                </div>
                <div className="mt-1 text-xs text-slate-500">{a.reason}{a.location && <span className="ml-2 inline-flex items-center gap-1"><MapPin className="h-3 w-3" />{a.location}</span>}</div>
                <div className="mt-2 flex flex-wrap gap-1"><AppointmentStatus status={a.status} testid={`day-appt-status-${a.id}`} /><PaymentBadge appt={a} testid={`day-appt-paid-${a.id}`} />{a.booked_by_patient && <span className="rounded-full bg-indigo-50 px-2 py-0.5 text-[11px] font-semibold text-indigo-700" data-testid={`day-appt-rebooked-${a.id}`}>Scelto dal paziente</span>}{a.rescheduled_to_label && <span className="text-[11px] text-slate-500">Spostato a {a.rescheduled_to_label}</span>}</div>
                <Link to={`/pazienti/${a.patient_id}`} onClick={(e) => e.stopPropagation()} className="mt-2 inline-block text-xs font-semibold text-[#3d655b]">Apri scheda</Link>
              </button>
            )) : <p className="text-sm text-slate-400">Nessun appuntamento.</p>}
          </div>
          <button className="btn-outline mt-4 w-full" onClick={() => { setEdit(null); setOpen(true); }} data-testid="day-panel-add-appointment"><CalendarPlus className="h-4 w-4" /> Aggiungi in questa data</button>
        </div>
      </div>
      <AppointmentDialog open={open} onOpenChange={setOpen} initial={edit} defaultDate={selected} onSaved={load} />
      <AvailabilityDialog open={avOpen} onOpenChange={setAvOpen} />
    </div>
  );
}
